import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CookieScanner = () => {
  const [cookies, setCookies] = useState([]);
  const [scanning, setScanning] = useState(false);

  const scanCookies = () => {
    setScanning(true);
    
    // Simulate scanning delay
    setTimeout(() => {
      const allCookies = document.cookie?.split(';')?.map(cookie => {
        const [name, value] = cookie?.trim()?.split('=');
        return { name, value };
      })?.filter(c => c?.name);

      // Add mock data for demonstration if no cookies found
      const scannedCookies = allCookies?.length > 0 ? allCookies : [
        { name: 'session_id', value: 'abc123...', purpose: 'Session management', expiry: 'Session', type: 'Essential' },
        { name: 'cookie_consent', value: 'true', purpose: 'Cookie preferences', expiry: '1 year', type: 'Essential' },
        { name: 'theme_preference', value: 'dark', purpose: 'UI customization', expiry: '1 year', type: 'Functional' }
      ];

      // Enhance with metadata
      const enhancedCookies = scannedCookies?.map(cookie => ({
        ...cookie,
        purpose: cookie?.purpose || 'Website functionality',
        expiry: cookie?.expiry || 'Session',
        type: cookie?.type || 'Unknown'
      }));

      setCookies(enhancedCookies);
      setScanning(false);
    }, 1000);
  };

  useEffect(() => {
    scanCookies();
  }, []);

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'essential':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'functional':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'analytics':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'marketing':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Active Cookies Scanner</h2>
          <p className="text-muted-foreground text-sm">
            View all cookies currently active on this website
          </p>
        </div>
        <button
          onClick={scanCookies}
          disabled={scanning}
          className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
        >
          {scanning ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Scanning...
            </>
          ) : (
            <>
              <Icon name="Search" size={18} />
              Scan Cookies
            </>
          )}
        </button>
      </div>

      {cookies?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-3 text-muted-foreground font-medium">Cookie Name</th>
                <th className="text-left py-3 px-3 text-muted-foreground font-medium">Type</th>
                <th className="text-left py-3 px-3 text-muted-foreground font-medium">Purpose</th>
                <th className="text-left py-3 px-3 text-muted-foreground font-medium">Expiry</th>
              </tr>
            </thead>
            <tbody>
              {cookies?.map((cookie, idx) => (
                <tr key={idx} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="py-3 px-3 font-mono text-xs text-foreground">{cookie?.name}</td>
                  <td className="py-3 px-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(cookie?.type)}`}>
                      {cookie?.type}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-muted-foreground">{cookie?.purpose}</td>
                  <td className="py-3 px-3 text-muted-foreground">{cookie?.expiry}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Cookie" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p>No cookies detected. Click "Scan Cookies" to check again.</p>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground">
          <Icon name="Info" size={14} color="var(--color-muted-foreground)" className="inline mr-1" />
          This scanner shows cookies stored by your browser for this website. Some cookies may not be visible due to browser security restrictions.
        </p>
      </div>
    </div>
  );
};

export default CookieScanner;